import m, {Vnode, VnodeDOM} from "mithril";
import _ from "underscore";
import $ from "jquery";
import {Questionary} from "../../model/questionary";

export type Answer = "negative"|"positive";

export interface RTMCardOptions {
    allowPositive?: boolean,
    allowNegative?: boolean,
    cardClass?: string,
    image?: string
}

export default class RTMCard
{
    // How many pixels to count the answer
    private static readonly MOVE_THRESHOLD = 10;
    private static readonly CONFIRM_THRESHOLD = 50;

    // Starting position of touch/mouse hold, just horizontal movement, x is enough
    private startingPosition: number|null = null;
    private shift: number = 0;

    private state: "waiting"|"focused"|"answered"|"canceling" = "waiting";
    private usingInputDevice: null|"pointer"|"keyboard" = null;

    private lastAnswer: Answer|null = null;
    private lastMaybe: Answer|null = null;

    private readonly options: RTMCardOptions;

    public constructor (
        private readonly question: string,
        private readonly answerCallback: (answer: Answer|null)=>void,
        private readonly onFinishCallback: ()=>void,
        private readonly questionary: Questionary,
        options?: RTMCardOptions
    ) {
        this.options = _.extend({
            allowPositive: true,
            allowNegative: true,
            cardClass: undefined,
            image: undefined
        }, options)

    }

    //-------------//
    // VIEW
    //
    public view() {

        const answer = this.answerMe();
        const maybe = this.maybeMe();

        return m(".swipe-area",{},
            this.viewBumper("positive", answer, maybe),
            this.viewBumper("negative", answer, maybe),

            m(this.getCardWrapperClass(), {},  [
                m('.card.shape'),
                m(this.getCardClass(), {
                    onpointerdown: (e: PointerEvent) => {
                        if (this.usingInputDevice === null) {
                            this.usingInputDevice = "pointer";
                            this.onStart(e)
                        }
                    },
                    oncontextmenu: (e: Event) => e.preventDefault(),
                    onpointermove: (e: PointerEvent) => {
                        if (this.usingInputDevice === "pointer") {
                            if (e.buttons === 0 && this.state === 'focused') {
                                this.onFinish(e);
                                this.usingInputDevice = null;
                            } else {
                                this.onMove(e);
                            }
                        }
                    },
                    ontouchmove: (e: TouchEvent) => e.preventDefault(),
                    onpointerup: (e: PointerEvent) => {
                        if (this.usingInputDevice === "pointer") {
                            this.onFinish(e)
                            this.usingInputDevice = null;
                        }
                    },
                    onpointercancel: () => {
                        this.onCancel();
                        this.usingInputDevice = null;
                    },
                    id: 'question',
                }, [
                    this.viewCardBody()
                ])
            ])
        );
    }

    private viewCardBody() {
        return m('.card-body',  {
            draggable: false,
        }, [
            (this.options !== null && this.options.image) ? m("img", {
                draggable: false,
                src: this.options.image,
            }) : null,
            m('p', {}, m.trust(this.questionary.renderText(this.question))),
        ]);
    }

    private viewBumper(bumper: Answer, answer: Answer|null, maybe: Answer|null): Vnode {
        let cssClass = '.bumper.bumper-'+bumper;
        cssClass += answer===bumper ? '.active' : '';
        cssClass += maybe===bumper ? '.maybe' : '';
        return m(cssClass);
    }

    //-------------//
    // LOGIC
    //
    private getCardWrapperClass(): string {
        let cssClass = 'form.card-wrapper.'+this.state;
        const maybe = this.maybeMe();
        const answer = this.answerMe();
        if (maybe) {
            cssClass += '.maybe-'+maybe;
        }
        if (answer) {
            cssClass += '.answer-'+answer;
        }
        return cssClass;
    }

    private getCardClass(): string {
        let cssClass = '.card.control';
        if (this.options.cardClass) {
            cssClass += '.'+this.options.cardClass;
        }
        return cssClass;
    }

    private answerMe(): Answer|null {
        if (this.shift < -RTMCard.CONFIRM_THRESHOLD && this.options.allowNegative) {
             return 'negative';
        }
        if (this.shift > RTMCard.CONFIRM_THRESHOLD && this.options.allowPositive) {
             return 'positive';
        }
        return null;
    }

    private maybeMe(): Answer|null {
        if (this.shift < -RTMCard.MOVE_THRESHOLD && this.options.allowNegative) {
            return 'negative';
        }
        if (this.shift > RTMCard.MOVE_THRESHOLD && this.options.allowPositive) {
            return 'positive';
        }
        return null;
    }


    //-------------//
    // HANDLERS
    //
    private onStart(e: PointerEvent) {
        this.state = "focused";
        (<HTMLElement>e.currentTarget).setPointerCapture(e.pointerId);
        this.startingPosition = e.pageX;

    }

    private onMove(e: PointerEvent) {
        e.preventDefault();
        e.stopPropagation();
        if (this.startingPosition === null) {
            return;
        }

        let shift: number|null = null;

        if (window.TouchEvent && e instanceof TouchEvent && e.changedTouches.length) {
            shift = e.changedTouches[0].pageX - this.startingPosition;
        } else if (e instanceof MouseEvent) {
            shift = e.pageX - this.startingPosition;
        }

        if (shift !== null) {
            this.shift = shift;
        }

        const maybe = this.maybeMe();
        if (maybe !== this.lastMaybe) {
            this.lastMaybe = maybe;
        }

        const answer = this.answerMe();
        if (this.lastAnswer === null && answer !== null && window.navigator.vibrate) {
            window.navigator.vibrate(50);
        }
        if (answer !== this.lastAnswer) {
            this.answerCallback(answer);
            this.lastAnswer = answer;
        }
        this.onCardRotated($(e.currentTarget!));
    }

    private onFinish(e: PointerEvent) {
        (<HTMLElement>e.currentTarget).releasePointerCapture(e.pointerId);

        const answer = this.answerMe();
        e.preventDefault();

        const $card = $(e.currentTarget!);
        if (answer) {
            this.state = "answered";
            $card.on("animationend", (evnt) => {
                evnt.stopImmediatePropagation(); // prevent from multiple run
                this.onFinishCallback();
            });
            $card.addClass('fade-out-33-67');

        } else {
            this.state = "waiting";
            $card.removeClass('fade-out-33-67');
            this.startingPosition = null;
            this.shift = 0;
        }
        this.onCardRotated($card);
    }

    private onCardRotated($card: JQuery): void {
        $card.css('rotate', `${this.shift/20}deg`)
            .css('translate', `${this.shift}px`);
    }

    private onCancel() {
        this.startingPosition = null;
        this.shift = 0;

        this.onCardRotated($('.card.control#question'));
        m.redraw();
    }

    private onKeydown(e: KeyboardEvent) {
        let key = e.key?.toLowerCase();
        if (key === 'escape' && this.usingInputDevice === 'pointer') {
            this.onCancel();
        }
        if (key !== 'a' && key !== 'l') {
            return;
        }
        if (this.usingInputDevice !== null) {
            return;
        }
        if (this.state === "answered" || this.state === "focused") {
            return;
        }

        this.usingInputDevice = "keyboard";

        this.answerCallback(key === 'a' ? 'negative' : 'positive');

        this.shift = RTMCard.CONFIRM_THRESHOLD * 2 * (key === 'a' ? -1 : 1);
        this.onCardRotated($('.card.control#question')); // Sorry, global event does not target cart directly - e.currentTarget is not card element
        m.redraw();
    }

    private onKeyup(e: KeyboardEvent) {
        const key = e.key.toLowerCase();
        if (key !== 'a' && key !== 'l') {
            return;
        }
        if (this.usingInputDevice !== "keyboard") {
            return;
        }

        this.onFinishCallback();
        m.redraw();
    }

    oncreate(): void {

        // @ts-ignore
        $(document).on('keydown', this.onKeydown.bind(this));
        // @ts-ignore
        $(document).on('keyup', this.onKeyup.bind(this));
    }
}